import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { Grid, Stack } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const FlowBuilderClassificationModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [types, setTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [observation, setObservation] = useState("");

  const selectedType = types.find((t) => t.id === selectedTypeId);
  const categories = selectedType?.categories || [];

  useEffect(() => {
    if (open === "edit") {
      (async () => {
        try {
          const { data: result } = await api.get("/classification-types");
          const typesList = Array.isArray(result) ? result : (result.types || []);
          setTypes(typesList);

          const classification = data.data.classification;
          if (classification) {
            setSelectedTypeId(classification.typeId || "");
            setSelectedCategoryId(classification.categoryId || "");
            setObservation(classification.observation || "");
          }
          setActiveModal(true);
        } catch (error) {
          console.error("Erro ao carregar tipos de classificação:", error);
        }
      })();
    } else if (open === "create") {
      (async () => {
        try {
          const { data: result } = await api.get("/classification-types");
          const typesList = Array.isArray(result) ? result : (result.types || []);
          setTypes(typesList);
          setSelectedTypeId("");
          setSelectedCategoryId("");
          setObservation("");
          setActiveModal(true);
        } catch (error) {
          console.error("Erro ao carregar tipos de classificação:", error);
        }
      })();
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleTypeChange = (e) => {
    setSelectedTypeId(e.target.value);
    setSelectedCategoryId("");
  };

  const handleSave = () => {
    if (!selectedTypeId) {
      return toast.error("Selecione um tipo de classificação");
    }
    if (!selectedCategoryId) {
      return toast.error("Selecione uma categoria");
    }

    const type = types.find((t) => t.id === selectedTypeId);
    const category = categories.find((c) => c.id === selectedCategoryId);

    const classificationData = {
      typeId: selectedTypeId,
      categoryId: selectedCategoryId,
      typeName: type?.name || "",
      categoryName: category?.name || "",
      observation: observation || "",
    };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          classification: classificationData,
        },
      });
    } else if (open === "create") {
      onSave({
        classification: classificationData,
      });
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>
          {open === "create"
            ? "Adicionar classificação ao fluxo"
            : "Editar classificação"}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            <Grid container spacing={2} style={{ width: "100%" }}>
              <Grid item xs={12}>
                <Typography>Tipo de classificação</Typography>
                <Select
                  value={selectedTypeId}
                  onChange={handleTypeChange}
                  style={{ width: "100%", marginTop: 8 }}
                  displayEmpty
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (!selectedTypeId) return "Selecione um tipo";
                    const type = types.find((t) => t.id === selectedTypeId);
                    return type ? type.name : "Selecione um tipo";
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Selecione um tipo</em>
                  </MenuItem>
                  {types.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: type.color || "#999",
                          marginRight: 8,
                        }}
                      />
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <Typography>Categoria</Typography>
                <Select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  style={{ width: "100%", marginTop: 8 }}
                  displayEmpty
                  disabled={!selectedTypeId}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  }}
                  renderValue={() => {
                    if (!selectedCategoryId) return "Selecione uma categoria";
                    const cat = categories.find(
                      (c) => c.id === selectedCategoryId
                    );
                    return cat ? cat.name : "Selecione uma categoria";
                  }}
                >
                  <MenuItem value="" disabled>
                    <em>Selecione uma categoria</em>
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12}>
                <Typography>Observação (opcional)</Typography>
                <TextField
                  value={observation}
                  onChange={(e) => setObservation(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Observação sobre a classificação (suporta variáveis como ${variavel})"
                  style={{ marginTop: 8 }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary" variant="outlined">
              {i18n.t("contactModal.buttons.cancel")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              className={classes.btnWrapper}
              onClick={handleSave}
            >
              {open === "create" ? "Adicionar" : "Editar"}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderClassificationModal;
